import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ScrollArea, Button, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { CarouselDots } from '@app/shared/ui/molecules/Common';
import {
  UserAgreementSection,
  DevelopSection,
  StonesSection,
  ExpandSection,
  CompetitionSection,
  LevelUpSection,
} from '@app/shared/ui/organisms/Instructions';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { cn } from '@app/shared/utils/common';
import { Page } from '@app/routes';
import { INSTRUCTIONS_PRELOAD_IMAGES } from '@app/assets';
import {
  useGetCurrentUserProfileQuery,
  usersApi,
  useUpdateCurrentUserProfileMutation,
} from '@app/store/slices/users/usersApi';
import { useAppDispatch } from '@app/store/hooks';

export default function InstructionsPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isImagesLoading = usePreloadImages(INSTRUCTIONS_PRELOAD_IMAGES);
  const { data: userProfile, isLoading: isLoadingProfile } = useGetCurrentUserProfileQuery();
  const [updateCurrentUserProfile] = useUpdateCurrentUserProfileMutation();

  // If the agreement has already been read, skip the instructions
  useEffect(() => {
    if (!isLoadingProfile && userProfile && !userProfile.isUserAgreementRead) {
      void navigate(Page.HOME, { replace: true });
    }
  }, [isLoadingProfile, userProfile, userProfile?.isUserAgreementRead, navigate]);

  const incrementCurrentStep = () => {
    // If user is leaving the agreement step (step 1), mark as accepted
    if (currentStep === 1) {
      void updateCurrentUserProfile({
        isUserAgreementRead: true,
      })
        .unwrap()
        .then(() => {
          dispatch(
            usersApi.util.updateQueryData('getCurrentUserProfile', undefined, (draft) => {
              if (draft) {
                draft.isUserAgreementRead = true;
              }
            })
          );
        });
    }

    setCurrentStep(currentStep + 1);
  };

  const navigateToStonesShop = () => navigate(Page.COGNITION);

  if (isImagesLoading) {
    return <LoadingSpinner />;
  }

  const showGoToShop = [2, 3, 4, 5].includes(currentStep);
  const showContinue = [1, 2, 3, 4, 5].includes(currentStep);
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === 6;

  return (
    <div className="flex h-full flex-col bg-background-gradient px-[30px] py-12">
      <ScrollArea className="mb-4 py-5">
        <div className="flex flex-1 flex-col justify-center">
          {currentStep === 1 && <UserAgreementSection />}
          {currentStep === 2 && <DevelopSection />}
          {currentStep === 3 && <StonesSection />}
          {currentStep === 4 && <ExpandSection />}
          {currentStep === 5 && <CompetitionSection />}
          {currentStep === 6 && <LevelUpSection />}
        </div>
      </ScrollArea>

      <CarouselDots carouselLength={6} currentIndex={currentStep - 1} />
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {showGoToShop && (
          <Button shadow="blue" onClick={navigateToStonesShop}>
            {t('instructions.go_to_super_power')}
          </Button>
        )}
        {showContinue && (
          <Button
            shadow="blue"
            className={cn(isFirstStep && 'col-span-2')}
            onClick={incrementCurrentStep}
          >
            {t('common.continue')}
          </Button>
        )}
        {isLastStep && (
          <Button shadow="blue" className="col-span-2" onClick={navigateToStonesShop}>
            {t('instructions.activate_stone')}
          </Button>
        )}
      </div>
    </div>
  );
}
